import { v4 as uuidv4 } from 'uuid';

export enum OverlayTabName {
	Hcc = 'HCC',
	Clinical = 'CLINICAL',
	Documents = 'DOCUMENTS',
	Alerts = 'ALERTS',
	Ambient = 'AMBIENT',
}

export interface OverlayProtocol<T = unknown> {
	readonly destination: 'to-addon' | 'to-navina';
	readonly type: OverlayProtocolTypes | OpenSummaryProtocolTypes;
	readonly params: T;
	readonly tab?: OverlayTabName;
}

export enum OverlayProtocolTypes {
	// FromNavinaToAddonEvents
	'addon-change-frame-src' = 'addon-change-frame-src',
	'addon-sso-login-succeed' = 'addon-sso-login-succeed',
	'addon-cart-load' = 'addon-cart-load',
	'addon-open-page-full-screen' = 'addon-open-page-full-screen',
	'addon-push-diagnoses-request' = 'addon-push-diagnoses-request',
	'addon-reload-diagnoses' = 'addon-reload-diagnoses',
	'addon-sso-login-completed' = 'addon-sso-login-completed',
	'addon-update-overlay-notifications' = 'addon-update-overlay-notifications',
	'addon-push-cpts-request' = 'addon-push-cpts-request',
	'addon-refresh-hpi' = 'addon-refresh-hpi',
	'addon-refresh-physical-exam' = 'addon-refresh-physical-exam',
	'addon-refresh-patient-instructions' = 'addon-refresh-patient-instructions',
	'addon-ambient-recording-state-changed' = 'addon-ambient-recording-state-changed',
	'addon-refresh-ros' = 'addon-refresh-ros',
	'addon-refresh-assessment' = 'addon-refresh-assessment',
	// FromAddonToNavinaEvent
	'navina-add-diagnoses' = 'navina-add-diagnoses',
	'navina-diagnoses-changed' = 'navina-diagnoses-changed',
	'navina-push-diagnoses-result' = 'navina-push-diagnoses-result',
	'navina-push-cpts-result' = 'navina-push-cpts-result',
	'navina-inform-encounter-status' = 'navina-inform-encounter-status',
	'navina-tab-selected' = 'navina-tab-selected',
}

export enum OpenSummaryProtocolTypes {
	'open-summary' = 'open-summary',
}

class AddonCommunicator {
	private callbacks: { [eventListenerId: string]: (event: MessageEvent) => any } = {};

	constructor() {
		this.callbacks = {};
		this.startListening();
	}

	readonly addCallback = <T = unknown>(callback: (event: MessageEvent<OverlayProtocol<T>>) => void): string => {
		const eventListenerId = uuidv4();
		this.callbacks[eventListenerId] = callback;
		return eventListenerId;
	};

	readonly removeCallback = (eventListenerId: string): void => {
		const { [eventListenerId]: _removedCallback, ...restCallbacks } = this.callbacks;
		this.callbacks = restCallbacks;
	};

	readonly startListening = (): void => {
		const addonOrigin = new URLSearchParams(window.location.search).get('addonOrigin');
		console.log('>> Start listening');

		window.addEventListener('message', (event): void => {
			if (event.origin === addonOrigin && event.data.destination === 'to-navina') {
				console.log('Message arrived to Navina:', event);
			}
			Object.values(this.callbacks).forEach((callback) => callback(event));
		});
	};

	readonly sendToAddon = (eventType: OverlayProtocolTypes | OpenSummaryProtocolTypes, params: any = {}): void => {
		const message = {
			destination: 'to-addon',
			type: eventType,
			params: params || {},
		} as const satisfies OverlayProtocol;
		console.log('>> Message to addon:', message);
		window.parent.postMessage(message, '*');
	};
}

let maybeAddonCommunicator: AddonCommunicator | null = null;

function createAddonCommunicator(): AddonCommunicator {
	return new AddonCommunicator();
}

export function getOrCreateAddonCommunicator(): AddonCommunicator {
	if (maybeAddonCommunicator === null) {
		maybeAddonCommunicator = createAddonCommunicator();
	}
	return maybeAddonCommunicator;
}
